import React, { useContext, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { useThrottle } from '@wix/da-hooks/pkg/useThrottle';
import { useResizeObserver } from '@wix/da-hooks/pkg/useResizeObserver';
import {
  BiData,
  BiContextProvidedValue,
  CommentCancelClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { EditorContext } from '@wix/da-editor-tiptap/pkg/components/Editor/Context';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import AddCircleIcon from '@wix/da-ds/pkg/Icons/AddCircle/AddCircle';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import IconButton from '../../../Button/IconButton';
import { useIsCommentEditorSubmittable } from '../../../CommentEditor/hooks/useIsCommentEditorSubmittable';
import { CommentEditorSource } from '../../../CommentEditor/redux/types';
import { CommentEditorContext } from '../../../CommentEditor/context';
import CommentBoxToolbar from '../CommentBoxToolbar';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';

import s from './CommentBoxFooter.scss';

const RESIZE_THROTTLE_DELAY = 300;
const RESIZE_THROTTLE_OPTIONS = { trailing: true };
const COLLAPSE_BREAKPOINT_WIDTH = 454;
const COLLAPSE_BUTTON_WIDTH = 109; // The button and its paddings

export interface Props {
  onCancel?: () => void;
  onPost?: () => void;
  className?: string;
}

/**
 * The footer for our CommentBox editor.
 *
 * TODO: Refactor this more. There is room for this to be modularized.
 */
const CommentBoxFooter: React.FC<React.PropsWithChildren<Props>> = ({
  onCancel,
  onPost,
  className,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { ref: editor } = useContext(EditorContext);
  const doneResize = useRef(false);
  const isEditorDebug = useFlag('editor_debug');

  const {
    isBusy,
    isExpanded: isEditorExpanded,
    editorSource,
  } = useContext(CommentEditorContext);
  const isSubmittable = useIsCommentEditorSubmittable();
  const isEdit = editorSource === CommentEditorSource.Edit;
  const [isToolbarCollapsable, setIsToolbarCollapsable] = useState(isMobile);
  const [isToolbarExpanded, setIsToolbarExpanded] = useState(false);
  const rootRef = useRef<HTMLElement>(null);
  const [rootWidth, setRootWidth] = useState(0);
  const handleResize = useThrottle(
    () => {
      if (!rootRef.current || (doneResize.current && isEditorDebug)) {
        return;
      }
      doneResize.current = true;

      const newWidth = rootRef.current.clientWidth;
      if (newWidth !== rootWidth) {
        setRootWidth(newWidth);
        setIsToolbarCollapsable(newWidth < COLLAPSE_BREAKPOINT_WIDTH);
      }
    },
    [rootWidth],
    RESIZE_THROTTLE_DELAY,
    RESIZE_THROTTLE_OPTIONS
  );
  useResizeObserver(rootRef, handleResize);

  const handleToolbarClick = useCallback((ev: React.MouseEvent) => {
    yieldToMain(() => {
      setIsToolbarExpanded(false);
    });
  }, []);

  return (
    <ErrorBoundary debugComponent="CommentBoxFooter">
      <footer
        className={classnames(
          s['root'],
          isEditorExpanded && s['editor-expanded'],
          isToolbarExpanded &&
            isEditorExpanded &&
            !isBusy &&
            s['expanded-toolbar'],
          isToolbarCollapsable && s['collapsable-toolbar'],
          className
        )}
        ref={rootRef}
      >
        <div className={s['toolbar-wrapper']}>
          {editor && (
            <>
              <IconButton
                tabIndex={isEditorExpanded ? 0 : -1}
                className={s['toolbar-toggle']}
                onClick={() => setIsToolbarExpanded(!isToolbarExpanded)}
                aria-label={t('commentbox.a11y.button.expandToolbar')}
              >
                <AddCircleIcon size={IconSize.MEDIUM} aria-hidden="true" />
              </IconButton>
              <div className={s['toolbar']} onClick={handleToolbarClick}>
                <CommentBoxToolbar
                  width={
                    isToolbarCollapsable
                      ? Math.max(rootWidth - COLLAPSE_BUTTON_WIDTH, 50)
                      : undefined
                  }
                />
              </div>
            </>
          )}
        </div>
        <div className={s['right']}>
          <TextButton
            variant="main"
            size="small"
            tabIndex={isEditorExpanded ? 0 : -1}
            disabled={isBusy}
            onClick={onCancel}
            biData={BiData<CommentCancelClickBiEvent>({
              evid: 189,
              itemid: BiContextProvidedValue,
              typeid: BiContextProvidedValue,
              is_comment_thread: BiContextProvidedValue,
              is_sticky: 0,
            })}
          >
            {t('commentbox.button.cancel')}
          </TextButton>
          <TextButton
            variant="approval-green"
            size="medium"
            tabIndex={isEditorExpanded ? 0 : -1}
            disabled={!isSubmittable}
            onClick={onPost}
          >
            {isEdit
              ? t('commentbox.button.update')
              : t('commentbox.button.comment')}
          </TextButton>
        </div>
      </footer>
    </ErrorBoundary>
  );
};

export default CommentBoxFooter;
